export const FDSPhase = {
    /** 초기 상태 */
    INACTIVE: 'INACTIVE',
    /** 이전 fds 진행 단계 복구중 */
    RESTORING: 'RESTORING',
    /** 기본 상태로 initializing 중 */
    INITIALIZING: 'INITIALIZING',
    /** 데이터 입력 가능 상태 */
    IDLE: 'IDLE',
    /** 인증과정 시작 */
    STARTED: 'STARTED',
    /** 인증과정 진행중 */
    PROCESSING: 'PROCESSING',
    /** 인증과정 종료 */
    FINISH: 'FINISH',
} as const

export type FDSPhaseType = Constant<typeof FDSPhase>

/** FDS 인증 결과 코드 */
export const FDSResultCode = {
    /** 성공 */
    SUCCEED: '00',
    /** 필수 입력값 누락 */
    MISSING_REQUIRED: '01',
    /** 기타에러 */
    ETC_ERROR: '02',
} as const

export type FDSResultCodeType = Constant<typeof FDSResultCode>

/** FDS 인증 통과 등급 */
export const FDS_PASS_AUTH_LEVEL = {
    /** 무인증 */
    SKIP_AUTH: 'ATH001',
    /** 암호/생체인증 */
    PASSWORD: 'ATH002',
    /** ARS 인증 */
    ARS: 'ATH003',
    /** 3DS 인증 */
    THREE_DS: 'ATH101',
} as const

/** FDS 인증 차단 등급 */
export const FDS_BLOCK_AUTH_LEVEL = {
    /** 결제한도 초과 */
    SELF_BLOCK: 'ATH098',
    /** 차단 */
    BLOCK: 'ATH099',
} as const

/** FDS 인증 등급 */
export const FDS_AUTH_LEVEL = {
    ...FDS_PASS_AUTH_LEVEL,
    ...FDS_BLOCK_AUTH_LEVEL,
} as const

export type FDSAuthLevel = Constant<typeof FDS_AUTH_LEVEL>
export type PassFDSAuthLevel = Constant<typeof FDS_PASS_AUTH_LEVEL>
export type BlockFDSAuthLevel = Constant<typeof FDS_BLOCK_AUTH_LEVEL>

export const isFDSPass = (level: FDSAuthLevel): level is PassFDSAuthLevel => {
    return Object.values(FDS_PASS_AUTH_LEVEL).includes(level as PassFDSAuthLevel)
}

/**
 * FDS 블로킹 원인
 * 참고) AML, CDD 관련 문서 : https://oss.fin.navercorp.com/point-benefit-fe/paylater-web/wiki/%ED%9B%84%EB%B6%88%EA%B2%B0%EC%A0%9C-%EC%9A%A9%EC%96%B4-%EC%84%A4%EB%AA%85
 * */
export const FDS_BLOCK_REASON = {
    /** AML EDD 아이디 결제불가 */
    UNABLE_TO_PAYMENT_FOR_AML_EDD: 'BLK001',
    /** AML CDD 아이디 결제불가 */
    UNABLE_TO_PAYMENT_FOR_AML_CDD: 'BLK002',
    /** FDS 개인정보 노출자 아이디 결제불가 */
    UNABLE_TO_PAYMENT_FOR_PRIVACY_EXPOSURE: 'BLK003',
    /** FDS 거래대상 아이디 결제불가 */
    UNABLE_TO_PAYMENT_FOR_TRANSACTION_TARGET: 'BLK004',
    /** FDS 본인 아이디 결제불가 */
    UNABLE_TO_PAYMENT_FOR_SELF_ID: 'BLK005',
    /** 채권 압류 아이디 결제불가 */
    UNABLE_TO_PAYMENT_FOR_SEIZURE_OF_BOND: 'BLK006',
    /** 알수없는 type */
    UNKNOWN: 'null',
} as const

export type FDSBlockReasonValues = Constant<typeof FDS_BLOCK_REASON>

export const FDSResult = {
    FAILED: 'failed',
    SUCCEED: 'succeed',
} as const

export type FDSResultType = Constant<typeof FDSResult>

/** fds session storage key값 */
export const SessionStorageKey = {
    /** fds 스토리지 */
    FDS_SESSION_STORAGE: 'FDS_SESSION_STORAGE',
} as const

/** 비번/생체인증, ARS 결과 페이지의 status param */
export const FDSBridgeStatus = {
    CANCELED: 'canceled',
    FAILED: 'failed',
    SUCCEED: 'succeed',
}

export type FDSBridgeStatusType = Constant<typeof FDSBridgeStatus>

/** FDS 검수 대분류 코드
 * FDSInspectionCodeType
 * */
export const FDSInspectionCode = {
    /**
     * 처벌 (READ BLOCK에 해당)
     */
    PUNISHED: 'ITC001',
    /**
     * 경고
     */
    ADVISED: 'ITC002',
    /**
     * 의심 (ARS 대상 및 추적 대상)
     */
    OBSERVED: 'ITC003',
    /**
     * 승인
     */
    APPROVED: 'ITC004',
    /**
     * 복원
     */
    RESTORED: 'ITC005',
    /**
     * 랭킹제외
     */
    RANK_NOT_COUNT: 'ITC006',
} as const

export type FDSInspectionCodeType = Constant<typeof FDSInspectionCode>

// https://wiki.navercorp.com/pages/viewpage.action?pageId=535020237#id-18.FDS블랙리스트테이블코드-2.어뷰징유형코드(ATC)
export const FDSAbuseCode = {
    MERCHANT_ETC: 'ATC004000000',
    MERCHANT_RANK_ABUSE: 'ATC004001001',
    MERCHANT_REVIEW_ABUSE: 'ATC004001002',
    MERCHANT_DIRECT_DEAL_ABUSE: 'ATC004004002',
} as const

export type FDSAbuseCodeType = Constant<typeof FDSAbuseCode>

// ARS 사용 서비스 현황 docs: https://wiki.fin.navercorp.com/pages/viewpage.action?pageId=168425938
export const ARS_SERVICE_ID = {
    /** 주문서 (단건/정기) */
    ORDER_SHEET: 'FDS_ORDER_SHEET',
    /** 디콘 (단건/정기) */
    DICON: 'FDS_DCON',
    /** 인증형 주문서(단건/정기) */
    PARTNERS: 'FDS_AUTH_ORDER_SHEET',
}

export type ArsServiceIdType = Constant<typeof ARS_SERVICE_ID>
